import {
  useAcceptInvite,
  useCreateTrip,
  useDeclineInvite,
  useMyInvites,
  useTripItems,
  useTrips,
} from '@/api/trips';
import { Badge } from '@/components/library/ui/Badge';
import { Tabs } from '@/components/library/ui/Tabs';
import { tripProgress } from '@/components/trips/tripProgress';
import { Progress } from '@/components/ui/Progress';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import type { IncomingTripInvite, Trip } from '@ultimate-checklist/shared';
import { CalendarCheck, Check, Plus, UserPlus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/trips/')({
  component: TripsIndex,
});

type TripTab = 'active' | 'closed';

function TripsIndex() {
  useDocumentTitle('Trips');
  const [tab, setTab] = useState<TripTab>('active');
  const [createOpen, setCreateOpen] = useState(false);
  const activeTrips = useTrips('active');
  const closedTrips = useTrips('closed');
  const trips = tab === 'active' ? activeTrips : closedTrips;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">Trips</h1>
          <p className="text-sm text-[var(--muted-foreground)]">
            Build a working checklist from your library, then close it when the trip is over.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="self-start sm:self-auto">
          <Plus />
          New Trip
        </Button>
      </div>

      <InvitationsSection />

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as TripTab)}
        tabs={[
          { value: 'active', label: 'Active' },
          { value: 'closed', label: 'Archived' },
        ]}
      />

      <TripGrid trips={trips.data} isLoading={trips.isLoading} isError={trips.isError} tab={tab} />

      <CreateTripDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

/** Incoming trip invitations for the signed-in user — accept to gain access. */
function InvitationsSection() {
  const invites = useMyInvites();
  const accept = useAcceptInvite();
  const decline = useDeclineInvite();
  const navigate = useNavigate();

  if (!invites.data?.length) {
    return null;
  }

  async function onAccept(invite: IncomingTripInvite) {
    try {
      const trip = await accept.mutateAsync(invite.id);
      toast.success(`Joined “${trip.name}”`);
      void navigate({
        to: '/trips/$tripId',
        params: { tripId: trip.id },
        search: { view: 'overview' },
      });
    } catch {
      toast.error('Couldn’t accept the invitation');
    }
  }

  async function onDecline(inviteId: string) {
    try {
      await decline.mutateAsync(inviteId);
    } catch {
      toast.error('Couldn’t decline the invitation');
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-[var(--primary)]" />
          <h2 className="text-sm font-semibold">Invitations</h2>
          <Badge variant="secondary">{invites.data.length}</Badge>
        </div>
        <div className="flex flex-col gap-2">
          {invites.data.map((invite) => (
            <div
              key={invite.id}
              className="flex flex-col gap-3 rounded-lg border border-[var(--border)] p-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{invite.tripName}</p>
                <p className="truncate text-xs text-[var(--muted-foreground)]">
                  {invite.invitedByName || invite.invitedByEmail} invited you as {invite.role}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={accept.isPending}
                  onClick={() => void onAccept(invite)}
                >
                  <Check />
                  Accept
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={decline.isPending}
                  onClick={() => void onDecline(invite.id)}
                >
                  Decline
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function TripGrid({
  trips,
  isLoading,
  isError,
  tab,
}: {
  trips?: Trip[];
  isLoading: boolean;
  isError: boolean;
  tab: TripTab;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[0, 1].map((index) => (
          <div
            key={index}
            className="h-36 animate-pulse rounded-lg border border-[var(--border)] bg-[var(--muted)]"
          />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-[var(--muted-foreground)]">
          Couldn’t load trips.
        </CardContent>
      </Card>
    );
  }

  if (!trips || trips.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
          <CalendarCheck className="h-8 w-8 text-[var(--muted-foreground)]" />
          <p className="text-sm text-[var(--muted-foreground)]">
            {tab === 'active' ? 'No active trips yet.' : 'No archived trips yet.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {trips.map((trip) => (
        <TripCard key={trip.id} trip={trip} />
      ))}
    </div>
  );
}

function TripCard({ trip }: { trip: Trip }) {
  // Cached per trip; reused when the trip opens. Progress shows only once items load.
  const items = useTripItems(trip.id);
  const progress = items.data ? tripProgress(items.data) : null;
  const archived = trip.status === 'closed';

  // The whole card opens the trip; archive/restore lives on the trip's Manage page.
  return (
    <Link
      to="/trips/$tripId"
      params={{ tripId: trip.id }}
      search={{ view: 'overview' }}
      className="flex flex-col gap-2 rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 shadow-sm transition-colors hover:bg-[var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium">{trip.name}</p>
          <p className="text-sm text-[var(--muted-foreground)]">
            {archived && trip.closedAt
              ? `Archived ${formatDate(trip.closedAt)}`
              : `Updated ${formatDate(trip.updatedAt)}`}
          </p>
        </div>
        {archived ? (
          <span className="shrink-0 rounded-md border border-[var(--border)] px-2 py-1 text-xs text-[var(--muted-foreground)]">
            Archived
          </span>
        ) : null}
      </div>
      {progress && progress.total > 0 ? (
        <Progress label="Packed" value={progress.done} max={progress.total} className="pt-1" />
      ) : null}
    </Link>
  );
}

function CreateTripDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const createTrip = useCreateTrip();
  const navigate = useNavigate();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const trip = await createTrip.mutateAsync({ name });
      setName('');
      onOpenChange(false);
      await navigate({
        to: '/trips/$tripId',
        params: { tripId: trip.id },
        search: { view: 'overview' },
      });
    } catch {
      toast.error('Couldn’t create trip');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>New Trip</DialogTitle>
            <DialogDescription>
              Create a working checklist from your reusable lists.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="trip-name">Name</Label>
            <Input
              id="trip-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Summer trip to Grandma's"
              required
            />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={createTrip.isPending || name.trim().length === 0}>
              <Plus />
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(
    new Date(value),
  );
}
